import React, { useMemo, useState } from 'react';
import { ActionButton, Flex, Text } from '@adobe/react-spectrum';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import { getPrettyCategoryLabel as prettyCategory } from '../../../data/CategoryPrettyDisplayUtils';
import { CardStatList, CARD_STAT_LIST_ADAPTERS } from '../../common/cardStats/CardStatList';
import { FilterBanner } from '../../common/cardStats/FilterBanner';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatNumber, formatPct } from '../../common/cardStats/formatters';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';

/**
 * Soft deny list — when the key category is present on a combo, the listed
 * sub-categories are suppressed from both the plan-mix rollup and the per-
 * combo chip row. Use this when two tags always co-occur and the dominant
 * one communicates the idea (e.g. `win-game` subsumes `exile-your-library`
 * for the Thoracle / Consultation style combos).
 */
const CATEGORY_SOFT_DENY = {
    'win-game': ['exile-your-library'],
};

/**
 * Returns a combo's categories with the soft-deny rules applied.
 */
const effectiveComboCategories = (combo) => {
    const cats = new Set(combo?.categories || []);
    Object.entries(CATEGORY_SOFT_DENY).forEach(([dominant, suppressed]) => {
        if (cats.has(dominant)) {
            suppressed.forEach((s) => cats.delete(s));
        }
    });
    return Array.from(cats);
};

const pct = formatPct;

const ComboStatTile = StatMetricTile;

export function CombosTab({ bin, onSelectCard }) {
    const combos = bin?.combos || [];
    const comboStats = bin?.comboStats;
    const [activeCategory, setActiveCategory] = useState(null);

    const filteredCombos = useMemo(() => {
        if (!activeCategory) return combos;
        return combos.filter((combo) => (combo.categories || []).includes(activeCategory));
    }, [combos, activeCategory]);

    const categoryEntries = useMemo(() => {
        const cats = comboStats?.categories || [];
        // Drop suppressed categories when their dominant counterpart is
        // present in the same bin — the aggregate line would otherwise
        // double-report the same combos (e.g. `exile-your-library` is
        // nearly always a subset of `win-game` wincon combos).
        const present = new Set(cats.map((c) => c.category));
        const suppressed = new Set();
        present.forEach((cat) => {
            if (CATEGORY_SOFT_DENY[cat]) {
                CATEGORY_SOFT_DENY[cat].forEach((s) => suppressed.add(s));
            }
        });
        return cats
            .filter((entry) => !suppressed.has(entry.category))
            .sort((a, b) => {
                const rate = (b.deckRate || 0) - (a.deckRate || 0);
                if (rate !== 0) return rate;
                return (b.instanceCount || 0) - (a.instanceCount || 0);
            });
    }, [comboStats]);

    if (combos.length === 0) {
        return <Text UNSAFE_className="commanderCardStatsEmpty">No combos found in this bucket.</Text>;
    }

    return (
        <Flex direction="column" rowGap={20} marginTop={10}>
            <HeroPanel
                tone="gold"
                eyebrow="Combo Profile"
                title="Combo lines in play"
                hint="Combos this commander's decks are leveraging at this level"
                aside={
                    <HeroPanelTotal
                        label="Decks"
                        value={bin?.deckCount?.toLocaleString() ?? '—'}
                    />
                }
            />
            {/* ----- HEADER STATS ----- */}
            {comboStats && (
                <div className="statsMetricsGrid">
                    <ComboStatTile
                        label="Decks w/ combo"
                        value={`${comboStats.decksWithAnyCombo}/${bin.deckCount}`}
                        hint={pct(comboStats.decksWithAnyComboRate)}
                    />
                    <ComboStatTile
                        label="Wincon decks"
                        value={`${comboStats.decksWithWinconCombo}/${bin.deckCount}`}
                        hint={pct(comboStats.decksWithWinconComboRate)}
                    />
                    <ComboStatTile
                        label="Avg combos / deck"
                        value={formatNumber(comboStats.avgCombosPerDeck, 1)}
                        hint={`${comboStats.totalComboInstances || 0} total`}
                    />
                    <ComboStatTile
                        label="Wincon reliance"
                        value={pct(comboStats.winconInstanceShare)}
                        hint={`${comboStats.winconComboInstances || 0} of ${
                            comboStats.totalComboInstances || 0
                        }`}
                    />
                </div>
            )}

            {/* ----- PLAN MIX ----- */}
            {categoryEntries.length > 0 && (
                <StatsSection
                    title="Combo plan mix"
                    subtitle="Which infinite-* plans this commander tends to build. Click a category to filter the combo list."
                >
                    <div className="comboCategoryList">
                        {categoryEntries.map((entry) => {
                            const deckPct = Math.round((entry.deckRate || 0) * 100);
                            const isActive = activeCategory === entry.category;
                            return (
                                <button
                                    key={entry.category}
                                    type="button"
                                    className={`comboCategoryRow ${isActive ? 'is-active' : ''}`}
                                    onClick={() =>
                                        setActiveCategory(isActive ? null : entry.category)
                                    }
                                    title={`${entry.instanceCount || 0} instances (${pct(
                                        entry.instanceShare
                                    )} of all combos) across ${entry.deckCount || 0} decks`}
                                >
                                    <span className="comboCategoryName">
                                        {prettyCategory(entry.category)}
                                    </span>
                                    <div className="comboCategoryBarTrack">
                                        <div
                                            className="comboCategoryBarFill"
                                            style={{ width: `${Math.max(deckPct, 1)}%` }}
                                        />
                                    </div>
                                    <span className="comboCategoryPct">{`${deckPct}%`}</span>
                                    <span className="comboCategoryCount">
                                        {`${entry.deckCount || 0}/${bin.deckCount}`}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </StatsSection>
            )}

            {/* ----- COMBO LIST ----- */}
            <Flex direction="column" rowGap={4}>
                {activeCategory && (
                    <FilterBanner
                        label="Filtering by"
                        value={prettyCategory(activeCategory)}
                        detail={`${filteredCombos.length} of ${combos.length} combos`}
                        onClear={() => setActiveCategory(null)}
                        ariaLabel="Clear category filter"
                    />
                )}
                <CardStatList
                    cards={filteredCombos}
                    deckCount={bin.deckCount}
                    onSelectCard={onSelectCard}
                    resetKey={`combos:${activeCategory || 'all'}:${bin?.binKey}`}
                    getName={CARD_STAT_LIST_ADAPTERS.combo.getName}
                    getTypeSet={CARD_STAT_LIST_ADAPTERS.combo.getTypeSet}
                    searchPlaceholder="Filter combos by card name…"
                    searchAriaLabel="Filter combos by card name"
                    loadMoreLabel={(remaining) => `Show more combos (${remaining} remaining)`}
                    emptyMessage="No combos match these filters."
                    emptyMessageUnfiltered={
                        activeCategory
                            ? 'No combos match this category in the current bucket.'
                            : 'No combos found in this bucket.'
                    }
                    renderCard={(combo) => (
                        <ComboRow
                            key={combo.comboId}
                            combo={combo}
                            deckCount={bin.deckCount}
                            onSelectCard={onSelectCard}
                        />
                    )}
                />
            </Flex>
        </Flex>
    );
}

function ComboRow({ combo, deckCount, onSelectCard }) {
    const pctValue = Math.round((combo.inclusionRate || 0) * 100);
    const categoriesForRow = effectiveComboCategories(combo);
    const sortedCards = [...(combo.cards || [])].sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '')
    );
    const isWincon = combo.type === 'wincon';
    const categories = categoriesForRow;

    return (
        <div className={`comboRow ${isWincon ? 'is-wincon' : ''}`}>
            <div className="comboRowCards">
                {(isWincon || categories.length > 0) && (
                    <div className="comboRowTags">
                        {isWincon && <span className="comboTypeBadge">Wincon</span>}
                        {categories.map((cat) => (
                            <span key={cat} className="comboCategoryChip">
                                {prettyCategory(cat)}
                            </span>
                        ))}
                    </div>
                )}
                {sortedCards.map((card) => (
                    <ComboCardChip
                        key={`${combo.comboId}_${card.cardId}`}
                        card={card}
                        onSelectCard={onSelectCard}
                    />
                ))}
            </div>
            <div className="comboRowMeta">
                <div className="comboRowBarTrack">
                    <div className="comboRowBarFill" style={{ width: `${Math.max(pctValue, 1)}%` }} />
                </div>
                <span className="comboRowPercent">{`${pctValue}%`}</span>
                <span className="comboRowCount">{`${combo.count}/${deckCount}`}</span>
                {combo.spellbookUri && (
                    <ActionButton
                        onPress={() => window.open(combo.spellbookUri, '_blank', 'noopener')}
                        aria-label="Open on Commander Spellbook"
                        UNSAFE_style={{ width: 28, height: 28 }}
                        data-role="icon-only"
                    >
                        <LinkOut />
                    </ActionButton>
                )}
            </div>
        </div>
    );
}

function ComboCardChip({ card, onSelectCard }) {
    const name = card?.name || card?.cardId || 'Unknown';
    const imageUri = card?.imageUri;
    const cardId = card?.cardId || card?.id;

    const handleClick = () => {
        if (onSelectCard && cardId) onSelectCard({ id: cardId, cardId, name, imageUri, types: card?.types });
    };

    return (
        <span
            className="comboCardChip"
            onClick={handleClick}
            style={{ cursor: cardId ? 'pointer' : 'default' }}
            title={cardId ? `Click to view ${name}` : name}
        >
            {imageUri ? (
                <img src={imageUri} alt={name} className="comboCardThumb" loading="lazy" />
            ) : (
                <span className="comboCardThumbPlaceholder" />
            )}
            <span className="comboCardName">{name}</span>
        </span>
    );
}
